import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubjectLanguageVariants } from '@features/user';
import type { AudioInputDeviceService } from '@services/audio';
import { useEventTracking } from '@services/statsig';
import { AnimatePresence, motion } from 'motion/react';
import { useSnackbar } from 'notistack';

import type { Patient } from '../../../types/types';
import { useTelehealthCallManager } from '../context/useTelehealthCallManager';
import type { TelehealthEncounterRouteState } from '../types';
import { copyToClipboard } from '../utils';

import { ActiveCallElsewhereBanner } from './ActiveCallElsewhereBanner';
import { TelehealthActiveSessionCard } from './TelehealthActiveSessionCard';
import { TelehealthJoinBox } from './TelehealthJoinBox';
import { WaitingRoomInviteSendAdapter } from './WaitingRoomInviteSendAdapter';

interface TelehealthContainerProps {
  appointmentId?: string;
  encounterId: string;
  patientId?: string | null;
  patient?: Patient | null;
  nbf?: number | string;
  exp?: number | string;
  audioInputDeviceService: AudioInputDeviceService | null;
  canStartCall?: boolean;
  onStartBlocked?: () => void;
}

const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};
const fadeDuration = 0.2;

export function TelehealthContainer({
  appointmentId,
  encounterId,
  patientId,
  patient,
  nbf,
  exp,
  audioInputDeviceService,
  canStartCall = true,
  onStartBlocked,
}: TelehealthContainerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { singularCap } = useSubjectLanguageVariants();
  const { telehealth } = useEventTracking();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    session,
    phase,
    activeEncounterId,
    joinCallDirect,
    leaveCall,
    waitingRoomLaunchIntent,
    waitingRoom,
  } = useTelehealthCallManager();

  const isThisEncounter = activeEncounterId === encounterId;
  const autoJoinAttemptedRef = useRef<string | null>(null);
  const stopPreviewRef = useRef<(() => void) | null>(null);
  const encounterRouteState = location.state as TelehealthEncounterRouteState | null;
  const isWaitingRoomLaunchIntentBound = waitingRoomLaunchIntent?.encounterId === encounterId;
  const shouldAutoJoinWaitingRoomLaunch =
    encounterRouteState?.telehealthLaunch?.source === 'waiting-room' &&
    encounterRouteState.telehealthLaunch.skipPreCallLobby !== false &&
    isWaitingRoomLaunchIntentBound;
  const blockedStartLabel = `Start call and invite ${singularCap.toLowerCase()}`;

  const [waitingRoomLinkCopied, setWaitingRoomLinkCopied] = useState(false);
  const [showWaitingRoomSendDialog, setShowWaitingRoomSendDialog] = useState(false);

  const handleWaitingRoomCopyLink = useCallback(async () => {
    const source = session ? 'active_session_card' : 'prejoin_card';
    const metadata = {
      appointment_id: appointmentId,
      encounter_id: encounterId,
      source,
    };

    if (!session) {
      telehealth.prejoinCopyLinkClicked(metadata);
    }

    const waitingRoomUrl = waitingRoom.link?.short_url;
    if (!waitingRoomUrl) {
      telehealth.waitingRoomLinkCopyAttempted({
        ...metadata,
        outcome: 'unavailable',
        has_link: false,
      });
      enqueueSnackbar('Waiting room link is not available yet.', { variant: 'warning' });
      return;
    }

    try {
      await copyToClipboard(waitingRoomUrl);
      telehealth.waitingRoomLinkCopyAttempted({
        ...metadata,
        outcome: 'success',
        has_link: true,
      });
      telehealth.linkCopied(metadata);
      setWaitingRoomLinkCopied(true);
      window.setTimeout(() => setWaitingRoomLinkCopied(false), 2000);
      enqueueSnackbar('Waiting room link copied.', { variant: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Clipboard copy failed';
      telehealth.waitingRoomLinkCopyAttempted({
        ...metadata,
        outcome: 'failed',
        has_link: true,
        error_message: errorMessage,
      });
      telehealth.linkCopyFailed({
        appointment_id: appointmentId,
        error_message: errorMessage,
      });
      enqueueSnackbar('Could not copy waiting room link.', { variant: 'error' });
    }
  }, [
    appointmentId,
    encounterId,
    enqueueSnackbar,
    session,
    telehealth,
    waitingRoom.link?.short_url,
  ]);

  const handleWaitingRoomSendLink = useCallback(() => {
    const source = session ? 'active_session_card' : 'prejoin_card';
    telehealth.prejoinSendInviteClicked({
      appointment_id: appointmentId,
      encounter_id: encounterId,
      source,
      outcome: patientId ? 'dialog_opened' : 'missing_patient',
    });

    if (!patientId) {
      enqueueSnackbar(`Attach a ${singularCap.toLowerCase()} before sending an invite.`, {
        variant: 'warning',
      });
      return;
    }

    setShowWaitingRoomSendDialog(true);
  }, [appointmentId, encounterId, enqueueSnackbar, patientId, session, singularCap, telehealth]);

  const handleDirectJoin = useCallback(() => {
    if (!canStartCall) {
      onStartBlocked?.();
      return;
    }

    telehealth.joinClicked({
      appointment_id: appointmentId,
      encounter_id: encounterId,
      source: 'prejoin_card',
    });
    void joinCallDirect({ appointmentId, encounterId, source: 'prejoin_card' });
  }, [appointmentId, canStartCall, encounterId, joinCallDirect, onStartBlocked, telehealth]);

  const handlePreviewStopReady = useCallback((stopPreview: (() => void) | null) => {
    stopPreviewRef.current = stopPreview;
  }, []);

  // Notify on mic disconnect during active session
  useEffect(() => {
    if (!audioInputDeviceService || !session) return;
    const handler = () => {
      enqueueSnackbar('Microphone disconnected. Select a new microphone.', {
        variant: 'warning',
      });
    };
    audioInputDeviceService.on('deviceLost', handler);
    return () => {
      audioInputDeviceService.off('deviceLost', handler);
    };
  }, [audioInputDeviceService, session, enqueueSnackbar]);

  useEffect(() => {
    if (!canStartCall || !shouldAutoJoinWaitingRoomLaunch || phase !== 'idle' || session) {
      return;
    }

    const autoJoinKey = `${encounterId}:${appointmentId ?? 'walk-in'}`;
    if (autoJoinAttemptedRef.current === autoJoinKey) {
      return;
    }

    autoJoinAttemptedRef.current = autoJoinKey;
    stopPreviewRef.current?.();
    navigate(location.pathname, {
      replace: true,
      state: {
        ...(encounterRouteState ?? {}),
        telehealthLaunch: undefined,
      },
    });
    void joinCallDirect({ appointmentId, encounterId, source: 'auto_waiting_room' });
  }, [
    appointmentId,
    canStartCall,
    encounterRouteState,
    encounterId,
    joinCallDirect,
    location.pathname,
    navigate,
    phase,
    session,
    shouldAutoJoinWaitingRoomLaunch,
  ]);

  // --- Render ---

  // Active call for a DIFFERENT encounter
  if (session && activeEncounterId && !isThisEncounter) {
    return (
      <ActiveCallElsewhereBanner activeEncounterId={activeEncounterId} onEndCall={leaveCall} />
    );
  }

  const renderContent = () => {
    // State 1: Idle / joining → unified lobby with camera preview + device selectors.
    // Joining itself is surfaced in the floating telehealth panel.
    if (phase === 'idle' || phase === 'joining') {
      return (
        <AnimatePresence mode="popLayout">
          <motion.div
            key="join"
            layout
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{
              layout: { duration: 0.3, ease: 'easeInOut' },
              opacity: { duration: fadeDuration },
            }}
            style={{ width: '100%' }}
          >
            <TelehealthJoinBox
              appointmentId={appointmentId}
              nbf={nbf}
              exp={exp}
              onDirectJoin={handleDirectJoin}
              isJoining={phase === 'joining'}
              stopPreviewOnDirectJoin={canStartCall}
              joinButtonLabel={canStartCall ? undefined : blockedStartLabel}
              onCopyLink={waitingRoom.hasAccess ? handleWaitingRoomCopyLink : undefined}
              onSendLink={waitingRoom.hasAccess ? handleWaitingRoomSendLink : undefined}
              isCopyingLink={waitingRoom.isLoading}
              isSendingLink={waitingRoom.isSendingInvite}
              linkCopied={waitingRoomLinkCopied}
              onPreviewStopReady={handlePreviewStopReady}
              subjectLabel={singularCap}
            />
          </motion.div>
        </AnimatePresence>
      );
    }

    // State 2: Active call → session status card (call UI is in CallPanel)
    return (
      <AnimatePresence mode="popLayout">
        <motion.div
          key="active"
          layout
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{
            layout: { duration: 0.3, ease: 'easeInOut' },
            opacity: { duration: fadeDuration },
          }}
          style={{ width: '100%' }}
        >
          <TelehealthActiveSessionCard
            onCopyLink={handleWaitingRoomCopyLink}
            onSendLink={waitingRoom.hasAccess ? handleWaitingRoomSendLink : undefined}
            isCopyingLink={waitingRoom.isLoading}
            isSendingLink={waitingRoom.isSendingInvite}
            linkCopied={waitingRoomLinkCopied}
            showInviteControls={waitingRoom.hasAccess}
            subjectLabel={singularCap}
          />
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <motion.div
      layout="position"
      transition={{ layout: { duration: 0.3, ease: 'easeInOut' as const } }}
      style={{
        width: '100%',
        flex: 0,
      }}
    >
      {renderContent()}
      {showWaitingRoomSendDialog && patientId && (
        <WaitingRoomInviteSendAdapter
          open
          onClose={() => setShowWaitingRoomSendDialog(false)}
          encounterId={encounterId}
          patientId={patientId}
          patient={patient ?? undefined}
          waitingRoomUrl={waitingRoom.link?.short_url}
          source={session ? 'active_session_card' : 'prejoin_card'}
          notifyPatient={waitingRoom.notifyPatient}
        />
      )}
    </motion.div>
  );
}
